import { useState, useEffect } from 'react';
import API from '../api';

const fmt = v => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(v || 0);
const emptyForm = { fecha_inicio: '', fecha_vencimiento: '', entidad: 'NU', tipo: 'CDT', origen: 'APORTE', monto: '', tasa: '', valor_entregado: '', estado: 'Activo', notas: '' };
// CDT vence en una fecha; el bolsillo liquida a diario y no vence; la cuenta no renta.
const TIPOS_RF = [
  { key: 'CDT', label: 'CDT (a plazo)' },
  { key: 'BOLSILLO', label: 'Bolsillo (liquidación diaria)' },
  { key: 'CUENTA', label: 'Cuenta sin rendimiento' },
];
const inputStyle = { background: '#f4f7fa', border: '1px solid #e8eef2', color: '#08293f' };

const MESES_FILTRO = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

export default function CDT({ embedded = false, anio: anioProp }) {
  const anioBase = anioProp || new Date().getFullYear();
  const [fEstado, setFEstado] = useState('Activo');
  const [fAnio,   setFAnio]   = useState(anioBase);
  const [fMes,    setFMes]    = useState('');
  useEffect(() => { setFAnio(anioProp || new Date().getFullYear()); }, [anioProp]);
  const [cdts,     setCdts]     = useState([]);
  const [form,     setForm]     = useState(emptyForm);
  const [editId,   setEditId]   = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading,  setLoading]  = useState(false);

  const cargar = () => API.get('/cdts').then(r => setCdts(r.data));
  useEffect(() => { cargar(); }, []);

  // Un instrumento entra en un periodo si estuvo vivo en algun momento de el.
  const vivoEn = (c, desde, hasta) => {
    const ini = new Date(c.fecha_inicio);
    const fin = c.fecha_vencimiento ? new Date(c.fecha_vencimiento) : new Date(8640000000000000);
    return ini <= hasta && fin >= desde;
  };
  const cdtsFiltrados = cdts.filter(c => {
    if (fEstado !== 'Todos' && c.estado !== fEstado) return false;
    if (!fAnio) return true;
    const y = Number(fAnio);
    const desde = fMes === '' ? new Date(y, 0, 1) : new Date(y, Number(fMes), 1);
    const hasta = fMes === '' ? new Date(y, 11, 31, 23, 59, 59) : new Date(y, Number(fMes) + 1, 0, 23, 59, 59);
    return vivoEn(c, desde, hasta);
  });

  const activos   = cdtsFiltrados.filter(c => c.estado === 'Activo');
  const vencidos  = cdtsFiltrados.filter(c => c.estado === 'Vencido');
  const totalActivo      = activos.reduce((s, c)  => s + parseFloat(c.monto || 0), 0);
  // El valor de hoy ya viene capitalizado desde el backend.
  const valorHoy         = activos.reduce((s, c)  => s + parseFloat(c.valor_actual || c.monto || 0), 0);
  const interesCausado   = valorHoy - totalActivo;
  const totalRendimiento = vencidos.reduce((s, c) => s + parseFloat(c.interes_causado || 0), 0);

  async function guardar(e) {
    e.preventDefault(); setLoading(true);
    try {
      if (editId) await API.put(`/cdts/${editId}`, form);
      else        await API.post('/cdts', form);
      setShowForm(false); setEditId(null); setForm(emptyForm);
      cargar();
    } catch (err) { alert('Error: ' + (err.response?.data?.error || err.message)); }
    finally { setLoading(false); }
  }

  function editar(c) {
    setForm({ fecha_inicio: c.fecha_inicio?.slice(0,10), fecha_vencimiento: c.fecha_vencimiento?.slice(0,10),
      entidad: c.entidad, tipo: c.tipo || 'CDT', origen: c.origen || 'APORTE', monto: c.monto, tasa: c.tasa,
      valor_entregado: c.valor_entregado, estado: c.estado, notas: c.notas || '' });
    setEditId(c.id); setShowForm(true);
  }

  const diasRestantes = fv => Math.ceil((new Date(fv) - new Date()) / 86400000);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          {!embedded && <h1 style={{ fontSize: 20, fontWeight: 700, color: '#08293f', margin: 0 }}>CDT — Inversiones</h1>}
          <p style={{ fontSize: 11, color: '#5a7a8a', letterSpacing: '0.06em', marginTop: embedded ? 0 : 4 }}>CDT, bolsillos y cuentas. El interés se capitaliza sobre la tasa efectiva anual.</p>
        </div>
        <button className="btn-primary" onClick={() => { setForm(emptyForm); setEditId(null); setShowForm(true); }}>+ Nueva inversión</button>
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 3 }}>
          {['Activo', 'Vencido', 'Todos'].map(e => (
            <button key={e} onClick={() => setFEstado(e)} style={{
              padding: '5px 11px', fontSize: 11, fontWeight: 700, cursor: 'pointer', borderRadius: 4,
              border: `1px solid ${fEstado === e ? '#00b8e3' : '#dce6ef'}`,
              background: fEstado === e ? 'rgba(0,184,227,0.1)' : '#fff',
              color: fEstado === e ? '#0369a1' : '#5a7a8a', fontFamily: 'inherit',
            }}>{e === 'Activo' ? 'Activos' : e === 'Vencido' ? 'Finalizados' : 'Todos'}</button>
          ))}
        </div>
        <select className="input" value={fAnio} onChange={e => setFAnio(e.target.value)} style={{ width: 100, padding: '5px 8px', fontSize: 12 }}>
          <option value="">Todo el histórico</option>
          {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i).map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <select className="input" value={fMes} onChange={e => setFMes(e.target.value)} disabled={!fAnio} style={{ width: 125, padding: '5px 8px', fontSize: 12 }}>
          <option value="">Todo el año</option>
          {MESES_FILTRO.map((m, i) => <option key={m} value={i}>{m}</option>)}
        </select>
        <span style={{ fontSize: 11, color: '#5a7a8a' }}>
          {cdtsFiltrados.length} de {cdts.length}
        </span>
      </div>

      {/* KPIs */}
      <div className="cdt-kpi-grid" style={{ marginBottom: 16 }}>
        {[
          { label: 'Capital aportado',     value: fmt(totalActivo),      color: '#00b8e3', accent: true },
          { label: 'Vale hoy',             value: fmt(valorHoy),         color: '#0369a1' },
          { label: 'Interés causado',      value: fmt(interesCausado),   color: '#059669' },
          { label: 'Cobrado (liquidados)', value: fmt(totalRendimiento), color: '#5a7a8a' },
        ].map((k, i) => (
          <div key={i} style={{ background: k.accent ? '#08293f' : '#fff', border: k.accent ? '1px solid rgba(0,184,227,0.3)' : '1px solid #e8eef2', padding: '16px 18px', borderRadius: 2 }}>
            <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: k.accent ? 'rgba(0,184,227,0.6)' : '#5a7a8a', marginBottom: 6 }}>{k.label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: k.accent ? '#00b8e3' : k.color }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 500 }}>
            <div style={{ padding: 28 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
                <div>
                  <h2 style={{ fontSize: 14, fontWeight: 700, color: '#08293f', margin: 0, letterSpacing: '0.04em', textTransform: 'uppercase' }}>{editId ? 'Editar inversión' : 'Nueva inversión'}</h2>
                  <div style={{ width: 32, height: 2, background: '#00b8e3', marginTop: 6 }} />
                </div>
                <button onClick={() => setShowForm(false)} style={{ fontSize: 20, fontWeight: 300, color: '#b3b3b3', background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
              </div>
              <form onSubmit={guardar}>
                <div style={{ marginBottom: 14 }}><label className="label">Origen del capital</label>
                  <select className="input" value={form.origen} style={inputStyle}
                    onChange={e => setForm(f => ({ ...f, origen: e.target.value }))}>
                    <option value="APORTE">Aporte — plata nueva que entra</option>
                    <option value="RENOVACION">Renovación — viene de otro que venció</option>
                  </select>
                  <div style={{ fontSize: 10, color: '#6b8295', marginTop: 4 }}>
                    Reinvertir un CDT que venció no es capital nuevo. Marcarlo bien evita que se lea como si hubieras aportado más.
                  </div>
                </div>
                <div style={{ marginBottom: 14 }}><label className="label">Tipo</label>
                  <select className="input" value={form.tipo} style={inputStyle}
                    onChange={e => setForm(f => ({ ...f, tipo: e.target.value, ...(e.target.value !== 'CDT' ? { fecha_vencimiento: '' } : {}), ...(e.target.value === 'CUENTA' ? { tasa: 0 } : {}) }))}>
                    {TIPOS_RF.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </select></div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                  <div><label className="label">Fecha inicio</label>
                    <input className="input" type="date" value={form.fecha_inicio} style={inputStyle}
                      onChange={e => setForm(f => ({ ...f, fecha_inicio: e.target.value }))} required /></div>
                  <div><label className="label">{form.tipo === 'CDT' ? 'Fecha vencimiento' : 'Vencimiento (no aplica)'}</label>
                    <input className="input" type="date" value={form.fecha_vencimiento} style={inputStyle}
                      disabled={form.tipo !== 'CDT'}
                      onChange={e => setForm(f => ({ ...f, fecha_vencimiento: e.target.value }))} required={form.tipo === 'CDT'} /></div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                  <div><label className="label">Entidad</label>
                    <select className="input" value={form.entidad} style={inputStyle} onChange={e => setForm(f => ({ ...f, entidad: e.target.value }))}>
                      <option>NU</option><option>Bancolombia</option><option>Davivienda</option>
                    </select></div>
                  <div><label className="label">Tasa EA %</label>
                    <input className="input" type="number" step="0.01" value={form.tasa} style={inputStyle}
                      disabled={form.tipo === 'CUENTA'}
                      onChange={e => setForm(f => ({ ...f, tasa: e.target.value }))} required={form.tipo !== 'CUENTA'} /></div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                  <div><label className="label">Monto</label>
                    <input className="input" type="number" value={form.monto} style={inputStyle}
                      onChange={e => setForm(f => ({ ...f, monto: e.target.value }))} required /></div>
                  <div><label className="label">Valor entregado</label>
                    <input className="input" type="number" value={form.valor_entregado} style={inputStyle}
                      onChange={e => setForm(f => ({ ...f, valor_entregado: e.target.value }))} /></div>
                </div>
                <div style={{ marginBottom: 14 }}><label className="label">Notas</label>
                  <input className="input" value={form.notas || ''} style={inputStyle} maxLength={250}
                    placeholder="Origen del capital, fusiones, etc."
                    onChange={e => setForm(f => ({ ...f, notas: e.target.value }))} /></div>
                <div style={{ marginBottom: 20 }}><label className="label">Estado</label>
                  <select className="input" value={form.estado} style={inputStyle} onChange={e => setForm(f => ({ ...f, estado: e.target.value }))}>
                    <option>Activo</option><option>Vencido</option>
                  </select></div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button type="submit" disabled={loading} className="btn-primary" style={{ flex: 1, padding: 10 }}>{loading ? 'Guardando...' : editId ? 'Actualizar' : 'Guardar'}</button>
                  <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Cancelar</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Desktop tabla */}
      <div className="cdt-tabla-desktop card" style={{ overflowX: 'auto', padding: 0 }}>
        <table className="tbl">
          <thead>
            <tr>
              {['Entidad','Tipo','Inicio','Vencimiento','Monto','Tasa','Vale hoy','Interés','Estado','Días',''].map(h => (
                <th key={h} style={{ textAlign: ['Monto','Tasa','Valor entregado','Rendimiento'].includes(h) ? 'right' : 'left' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cdtsFiltrados.map(c => {
              const dias = c.estado === 'Activo' && c.fecha_vencimiento ? diasRestantes(c.fecha_vencimiento) : null;
              return (
                <tr key={c.id}>
                  <td style={{ fontWeight: 700, color: '#08293f' }}>{c.entidad}</td>
                  <td>
                    <span style={{ fontSize: 9, fontWeight: 800, padding: '2px 6px', borderRadius: 3,
                      background: c.tipo === 'BOLSILLO' ? 'rgba(139,92,246,0.12)' : c.tipo === 'CUENTA' ? '#f1f5f8' : 'rgba(0,184,227,0.12)',
                      color: c.tipo === 'BOLSILLO' ? '#7c3aed' : c.tipo === 'CUENTA' ? '#5a7a8a' : '#0369a1' }}>
                      {c.tipo || 'CDT'}
                    </span>
                  </td>
                  <td style={{ color: '#5a7a8a', fontSize: 12 }}>{c.fecha_inicio?.slice(0,10)}</td>
                  <td style={{ color: '#5a7a8a', fontSize: 12 }}>{c.fecha_vencimiento ? c.fecha_vencimiento.slice(0,10) : '—'}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: '#08293f' }}>{fmt(c.monto)}</td>
                  <td style={{ textAlign: 'right', color: '#5a7a8a' }}>{Number(c.tasa) ? `${c.tasa}%` : '—'}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: '#08293f' }}>
                    {fmt(c.valor_actual)}
                    {c.liquidado && <span style={{ display: 'block', fontSize: 9, color: '#5a7a8a', fontWeight: 400 }}>entregado</span>}
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: Number(c.interes_causado) >= 0 ? '#059669' : '#dc2626' }}>
                    {fmt(c.interes_causado)}
                    {!c.liquidado && c.rendimiento_proyectado != null && (
                      <span style={{ display: 'block', fontSize: 9, color: '#0369a1', fontWeight: 400 }}>
                        al vencer ~{fmt(c.rendimiento_proyectado)}
                      </span>
                    )}
                  </td>
                  <td>
                    <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.06em', padding: '2px 8px',
                      background: c.estado === 'Activo' ? 'rgba(0,184,227,0.12)' : '#f4f7fa',
                      color: c.estado === 'Activo' ? '#00b8e3' : '#5a7a8a' }}>
                      {c.estado.toUpperCase()}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {dias !== null && (
                      <span style={{ fontSize: 11, fontWeight: 700, color: dias <= 15 ? '#dc2626' : dias <= 30 ? '#b45309' : '#5a7a8a' }}>
                        {dias}d
                      </span>
                    )}
                  </td>
                  <td><button onClick={() => editar(c)} style={{ fontSize: 11, color: '#00b8e3', background: 'none', border: 'none', cursor: 'pointer' }}>Editar</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="cdt-cards-mobile">
        {cdtsFiltrados.map(c => {
          const dias = c.estado === 'Activo' ? diasRestantes(c.fecha_vencimiento) : null;
          return (
            <div key={c.id} style={{ background: '#fff', border: '1px solid #e8eef2', borderRadius: 2, padding: '14px 16px', marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div>
                  <div style={{ fontWeight: 700, color: '#08293f', fontSize: 14 }}>{c.entidad}</div>
                  <div style={{ fontSize: 11, color: '#5a7a8a', marginTop: 2 }}>{c.fecha_inicio?.slice(0,10)} → {c.fecha_vencimiento?.slice(0,10)}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.06em', padding: '2px 8px',
                    background: c.estado === 'Activo' ? 'rgba(0,184,227,0.12)' : '#f4f7fa',
                    color: c.estado === 'Activo' ? '#00b8e3' : '#5a7a8a' }}>
                    {c.estado.toUpperCase()}
                  </span>
                  {dias !== null && (
                    <span style={{ fontSize: 11, fontWeight: 700, color: dias <= 15 ? '#dc2626' : dias <= 30 ? '#b45309' : '#5a7a8a' }}>{dias}d</span>
                  )}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
                <div>
                  <div style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#5a7a8a', marginBottom: 2 }}>Monto</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#08293f' }}>{fmt(c.monto)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#5a7a8a', marginBottom: 2 }}>Tasa</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#08293f' }}>{c.tasa}%</div>
                </div>
                <div>
                  <div style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#5a7a8a', marginBottom: 2 }}>Valor entregado</div>
                  <div style={{ fontSize: 13, color: '#5a7a8a' }}>{Number(c.valor_entregado) ? fmt(c.valor_entregado) : '—'}</div>
                </div>
                <div>
                  <div style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#5a7a8a', marginBottom: 2 }}>
                    {c.rendimiento != null ? 'Rendimiento' : 'Proyectado'}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: c.rendimiento != null ? '#059669' : '#0369a1' }}>
                    {c.rendimiento != null ? fmt(c.rendimiento) : `~${fmt(c.rendimiento_proyectado)}`}
                  </div>
                </div>
              </div>
              <button onClick={() => editar(c)} style={{ fontSize: 11, fontWeight: 600, color: '#00b8e3', background: 'rgba(0,184,227,0.08)', border: '1px solid rgba(0,184,227,0.2)', padding: '5px 12px', cursor: 'pointer', borderRadius: 2 }}>Editar</button>
            </div>
          );
        })}
      </div>

      <style>{`
        .cdt-kpi-grid     { display: grid; grid-template-columns: repeat(4,1fr); gap: 8px; }
        .cdt-tabla-desktop{ display: block; }
        .cdt-cards-mobile { display: none; }
        @media (max-width: 768px) {
          .cdt-kpi-grid     { grid-template-columns: repeat(2,1fr); }
          .cdt-tabla-desktop{ display: none; }
          .cdt-cards-mobile { display: block; }
        }
      `}</style>
    </div>
  );
}
